/**
 * Every enum in ICULogbook, defined once.
 *
 * **Never renumber or rename a key.** Stored data depends on these strings, and
 * a trainee's seven-year logbook is the thing that breaks. Adding is fine.
 *
 * Labels are what the UI and the PDF show. Keys are what the database holds.
 */
/* ---------------------------------------------------------------- case type */
/**
 * The four kinds of case.
 *
 * The first three all describe a patient arriving, or not arriving, at the
 * unit: `admission` is one you took onto ICU, `ward_review` one you reviewed
 * elsewhere in the hospital and did not admit (the Excel logbook's
 * `Admitted: No` maps here — see IMPORT_SOURCES.md), and `remote_advice` a
 * phone call.
 *
 * `notable_icu_case` is the fourth and covers what none of those did: a
 * patient **already on the unit**, admitted by someone else, whom the trainee
 * had significant involvement with — the daily ward-round review, a role in
 * their deterioration or resuscitation, liaising with a specialty about them.
 * That is a large part of the clinical work an ICM trainee actually does and
 * it had nowhere to go until 2026-08-30.
 *
 * It is a fourth `caseType` rather than a fifth record type, unlike
 * Communication, because it is a Case in every respect — a patient, an
 * episode, an age band, a capability level — and above all because
 * **procedures nest inside a Case and nowhere else**. A deterioration on the
 * unit produces a central line and an intubation, and no standalone record
 * type could hold them.
 */
export const CASE_TYPES = [
    'admission',
    'ward_review',
    'remote_advice',
    'notable_icu_case',
];
export const CASE_TYPE_OPTIONS = [
    { key: 'admission', label: 'Admission' },
    { key: 'ward_review', label: 'Ward review' },
    { key: 'remote_advice', label: 'Remote advice' },
    { key: 'notable_icu_case', label: 'Notable ICU case' },
];
/**
 * The case types describing a patient already under the unit's care, where
 * `reasonsForReferral` holds the problems being treated rather than the
 * reasons anybody was referred. Only the form's label changes; the stored
 * keys are the same `ReferralReason` values, so every aggregation, the PDF's
 * case mix and the export are unaffected.
 */
export function isAlreadyAdmitted(caseType) {
    return caseType === 'notable_icu_case';
}
/* ----------------------------------------------------------------- age band */
export const AGE_BANDS = ['under_16', '16_30', '31_50', '51_70', '71_85', 'over_85'];
/**
 * Age is a band and never a number, so the record evidences breadth of patient
 * population while being structurally incapable of identifying anyone. Do not
 * add an "exact age" field, and do not narrow these bands.
 */
export const AGE_BAND_OPTIONS = [
    { key: 'under_16', label: 'Under 16' },
    { key: '16_30', label: '16–30' },
    { key: '31_50', label: '31–50' },
    { key: '51_70', label: '51–70' },
    { key: '71_85', label: '71–85' },
    { key: 'over_85', label: 'Over 85' },
];
/** The band that marks an entry paediatric. See `isPaediatric` in derive.ts. */
export const PAEDIATRIC_AGE_BAND = 'under_16';
/* --------------------------------------------------------- capability level */
export const CAPABILITY_LEVELS = [1, 2, 3, 4];
/**
 * The FICM 1–4 capability scale, verbatim from the ICM Curriculum 2021.
 *
 * This wording appears in the form's info popover and in the PDF legend. It is
 * reproduced exactly in DATA_MODEL.md and must match there, because a reader
 * who knows the curriculum will notice a paraphrase. Never call this
 * "supervision level" in code; the UI label is "Capability level".
 */
export const CAPABILITY_LEVEL_DESCRIPTORS = {
    1: 'Performs tasks under direct supervision. Requires considerable guidance and help to manage clinical problems or acute divergences from a plan.',
    2: 'Performs tasks in straightforward circumstances with sound basic knowledge. Requires help or guidance for more difficult or complicated situations.',
    3: 'Performs tasks in most circumstances with advanced knowledge. Only requires occasional advice, assistance, or specialist help in complex situations.',
    4: 'Independent consultant-level practice and expert knowledge.',
};
/* ----------------------------------------------------------- level of care */
/**
 * The ICS level of critical care, 0 to 3 — how sick the patient was, as
 * distinct from what was wrong with them.
 *
 * This is deliberately **not** overlapping with `ReferralReason`. That field
 * answers "what was the problem"; this one answers "how much support did they
 * need". A septic patient can be level 2 or level 3, and the pair says more
 * than either alone.
 *
 * Chosen over an organ-failure count and over a per-system organ-support list
 * because it is the vocabulary every UK unit already uses, and because it is
 * the one the logbooks trainees arrive with already carry — the Excel ICU
 * logbook has a `Level of care` column that this app used to fold into the
 * note for want of a field. See IMPORT_SOURCES.md.
 *
 * **0 is a real value.** Every check on this field must test `undefined`
 * explicitly; `if (careLevel)` silently discards a level 0 case.
 */
export const CARE_LEVELS = [0, 1, 2, 3];
export const CARE_LEVEL_OPTIONS = [
    { key: '0', label: 'Level 0' },
    { key: '1', label: 'Level 1' },
    { key: '2', label: 'Level 2' },
    { key: '3', label: 'Level 3' },
];
/**
 * What each level means, shown in the field's info dialog.
 *
 * Written as a concise summary of the ICS levels of adult critical care rather
 * than quoted, unlike `CAPABILITY_LEVEL_DESCRIPTORS`, which is verbatim
 * curriculum wording a trainee would notice a paraphrase of. Nobody reads the
 * level definitions that closely; they need the gist at the bedside.
 */
export const CARE_LEVEL_DESCRIPTORS = {
    0: 'Needs met by normal ward care.',
    1: 'At risk of deteriorating, or recently stepped down from higher care. Managed on a ward with advice and support from the critical care team.',
    2: 'Support for a single failing organ system, or detailed observation and intervention. Includes patients stepping down from level 3.',
    3: 'Advanced respiratory support alone, or support of two or more organ systems. Includes all multi-organ failure.',
};
export function isCareLevel(value) {
    return CARE_LEVELS.includes(value);
}
/** "Level 2". Used by the entry row, the PDF and the spreadsheet alike. */
export function careLevelLabel(level) {
    return `Level ${String(level)}`;
}
/* ------------------------------------------------------------------- side */
export const SIDES = ['left', 'right', 'bilateral'];
export const SIDE_OPTIONS = [
    { key: 'left', label: 'Left' },
    { key: 'right', label: 'Right' },
    { key: 'bilateral', label: 'Bilateral' },
];
/* ----------------------------------------------------------------- source */
export const SOURCES = ['manual', 'import', 'restore'];
/* -------------------------------------------------------- referral reason */
export const REFERRAL_REASONS = [
    'respiratory_failure',
    'airway_compromise',
    'haemodynamic_instability',
    'sepsis',
    'post_op_elective',
    'post_op_emergency',
    'cardiac_arrest',
    'reduced_consciousness',
    'seizures',
    'tbi',
    'stroke_ich',
    'aki_rrt',
    'metabolic_endocrine',
    'poisoning',
    'gi_bleeding',
    'liver_failure',
    'major_trauma',
    'major_haemorrhage',
    'obstetric',
    'haem_onc',
    'neutropenic_sepsis',
    'end_of_life',
    'organ_donation',
    'enhanced_monitoring',
    'other',
];
/** Display order is the order below, not alphabetical — it groups by system. */
export const REFERRAL_REASON_OPTIONS = [
    { key: 'respiratory_failure', label: 'Respiratory failure' },
    { key: 'airway_compromise', label: 'Airway compromise / need for intubation' },
    { key: 'haemodynamic_instability', label: 'Haemodynamic instability / shock' },
    { key: 'sepsis', label: 'Sepsis' },
    { key: 'post_op_elective', label: 'Post-operative (elective)' },
    { key: 'post_op_emergency', label: 'Post-operative (emergency)' },
    { key: 'cardiac_arrest', label: 'Cardiac arrest / post-ROSC' },
    { key: 'reduced_consciousness', label: 'Reduced consciousness' },
    { key: 'seizures', label: 'Seizures / status epilepticus' },
    { key: 'tbi', label: 'Traumatic brain injury' },
    { key: 'stroke_ich', label: 'Stroke / intracranial haemorrhage' },
    { key: 'aki_rrt', label: 'Acute kidney injury / need for RRT' },
    { key: 'metabolic_endocrine', label: 'Metabolic or endocrine emergency' },
    { key: 'poisoning', label: 'Poisoning / overdose' },
    { key: 'gi_bleeding', label: 'GI bleeding' },
    { key: 'liver_failure', label: 'Acute liver failure' },
    { key: 'major_trauma', label: 'Major trauma' },
    { key: 'major_haemorrhage', label: 'Major haemorrhage' },
    { key: 'obstetric', label: 'Obstetric emergency' },
    { key: 'haem_onc', label: 'Haematology / oncology emergency' },
    { key: 'neutropenic_sepsis', label: 'Immunosuppression / neutropenic sepsis' },
    { key: 'end_of_life', label: 'End-of-life care / treatment limitation' },
    { key: 'organ_donation', label: 'Organ donation' },
    { key: 'enhanced_monitoring', label: 'Enhanced monitoring only' },
    { key: 'other', label: 'Other' },
];
/* --------------------------------------------------------------- EDT/Teaching */
/**
 * What a trainee did with their Educational Development Time.
 *
 * Teaching is two keys rather than one key plus a "delivered or attended"
 * field. It costs the same single tap either way, and the two are different
 * kinds of evidence to an ARCP panel: teaching you deliver is HiLLO 4, and
 * teaching you attend is not. Splitting them here means the PDF can report
 * them separately without a second stored field to keep consistent.
 *
 * `course_conference` is deliberately distinct from `teaching_attended`: a
 * departmental teaching afternoon and a three-day national course are both
 * "attending teaching", and trainees list them separately in a portfolio.
 */
export const EDT_ACTIVITIES = [
    'teaching_delivered',
    'teaching_attended',
    'clinic',
    'self_directed_learning',
    'project_audit',
    'research',
    'course_conference',
    'simulation',
    'management',
    'exam_preparation',
    'other',
];
/** Display order groups the two teaching keys together, then the rest by frequency. */
export const EDT_ACTIVITY_OPTIONS = [
    { key: 'teaching_delivered', label: 'Teaching delivered' },
    { key: 'teaching_attended', label: 'Teaching attended' },
    { key: 'clinic', label: 'Clinic' },
    { key: 'self_directed_learning', label: 'Self-directed learning' },
    { key: 'project_audit', label: 'Project or audit' },
    { key: 'research', label: 'Research' },
    { key: 'course_conference', label: 'Course or conference' },
    { key: 'simulation', label: 'Simulation' },
    { key: 'management', label: 'Management or leadership' },
    { key: 'exam_preparation', label: 'Exam preparation' },
    { key: 'other', label: 'Other' },
];
/** The one key that evidences teaching a trainee gave rather than received. */
export const TEACHING_DELIVERED = 'teaching_delivered';
/**
 * Duration presets, in minutes. Stored as minutes so a half day is exact
 * rather than 0.5 of something, and so a custom value needs no second unit.
 *
 * Optional, like everything else on an EDT entry — a trainee who logs what
 * they did but not for how long is a supported user, and the PDF says how many
 * entries carried a duration rather than implying the hours are complete.
 */
export const EDT_DURATION_PRESETS = [
    { key: '60', label: '1 hour' },
    { key: '120', label: '2 hours' },
    { key: '240', label: 'Half day' },
    { key: '480', label: 'Full day' },
];
export const MINUTES_IN_HOUR = 60;
export const MINUTES_IN_DAY = 24 * 60;
/**
 * The ceiling on one entry's duration: 20 days.
 *
 * It used to be a single day, on the reasoning that "longer is two entries".
 * That was wrong for the things the activity list already names — a three-day
 * national course, a project or audit worked on across a term — and it made
 * the "days" unit below meaningless, since the only value it could hold was 1.
 * Raised 2026-08-31 when the custom duration gained hours and days.
 *
 * **Why 20 and not 30:** `duration_minutes` is a Postgres `smallint`, which
 * stops at 32,767. Twenty days is 28,800, which fits with headroom; thirty
 * days is 43,200, which does not. Raising this further means changing the
 * column type first, not just this number.
 */
export const MAX_EDT_DURATION_MINUTES = 20 * MINUTES_IN_DAY;
/**
 * The units a custom duration can be entered in. Storage is always minutes —
 * this is only how the number is typed, so that "3 days" does not have to be
 * worked out as 4320 by the person logging it.
 */
export const EDT_DURATION_UNITS = ['minutes', 'hours', 'days'];
export const EDT_DURATION_UNIT_MINUTES = {
    minutes: 1,
    hours: MINUTES_IN_HOUR,
    days: MINUTES_IN_DAY,
};
export const EDT_DURATION_UNIT_OPTIONS = [
    { key: 'minutes', label: 'Minutes' },
    { key: 'hours', label: 'Hours' },
    { key: 'days', label: 'Days' },
];
/**
 * The unit an existing duration is most naturally read in, so that reopening
 * an entry saved as "3 days" shows 3 days and not 4320 minutes.
 */
export function naturalDurationUnit(minutes) {
    if (minutes % MINUTES_IN_DAY === 0)
        return 'days';
    if (minutes % MINUTES_IN_HOUR === 0)
        return 'hours';
    return 'minutes';
}
/**
 * "45m", "1 hour", "2h 30m", "3 days", "1 day 30m" — used by the entry row,
 * the spreadsheet and the PDF alike.
 *
 * Days appear from 1440 minutes up, which is also where they started
 * mattering: before the cap was raised nothing could reach a day. Nothing is
 * rounded away — a remainder is always shown — because this renders both a
 * single entry and a total summed across a year, and a total that quietly
 * loses an hour is a total nobody can reconcile.
 */
export function formatDuration(minutes) {
    if (minutes >= MINUTES_IN_DAY) {
        const days = Math.floor(minutes / MINUTES_IN_DAY);
        const rest = minutes % MINUTES_IN_DAY;
        const label = days === 1 ? '1 day' : `${String(days)} days`;
        return rest === 0 ? label : `${label} ${formatDuration(rest)}`;
    }
    const hours = Math.floor(minutes / MINUTES_IN_HOUR);
    const rest = minutes % MINUTES_IN_HOUR;
    if (hours === 0)
        return `${String(rest)}m`;
    if (rest === 0)
        return hours === 1 ? '1 hour' : `${String(hours)} hours`;
    return `${String(hours)}h ${String(rest)}m`;
}
/* --------------------------------------------------------- communication */
/**
 * A significant discussion logged as its own entry — most often withdrawal or
 * withholding of treatment, but the category exists for the whole family of
 * conversations a trainee's portfolio needs evidence of. See PRD.md, which
 * named this exact list of examples as deferred rather than out of scope.
 */
export const COMMUNICATION_CATEGORIES = [
    'withdrawal_of_treatment',
    'breaking_bad_news',
    'goals_of_care',
    'organ_donation',
    'conflict_resolution',
    'other',
];
export const COMMUNICATION_CATEGORY_OPTIONS = [
    { key: 'withdrawal_of_treatment', label: 'Withdrawal or withholding of treatment' },
    { key: 'breaking_bad_news', label: 'Breaking bad news' },
    { key: 'goals_of_care', label: 'Goals of care / ceiling of treatment' },
    { key: 'organ_donation', label: 'Organ donation' },
    { key: 'conflict_resolution', label: 'Conflict or disagreement resolution' },
    { key: 'other', label: 'Other' },
];
/**
 * Who was present, by **role, never by name** — the field most likely to
 * invite a patient or family member's name if it were free text, so it is not
 * free text. Multi-select: a discussion with two family members and a
 * chaplain is one event.
 */
export const COMMUNICATION_PARTICIPANTS = [
    'patient',
    'next_of_kin',
    'other_family',
    'nursing_staff',
    'chaplaincy',
    'other_specialty',
    'interpreter',
];
export const COMMUNICATION_PARTICIPANT_OPTIONS = [
    { key: 'patient', label: 'Patient' },
    { key: 'next_of_kin', label: 'Next of kin' },
    { key: 'other_family', label: 'Other family member(s)' },
    { key: 'nursing_staff', label: 'Nursing staff' },
    { key: 'chaplaincy', label: 'Chaplaincy / spiritual care' },
    { key: 'other_specialty', label: 'Other specialty' },
    { key: 'interpreter', label: 'Interpreter used' },
];
export const COMMUNICATION_OUTCOMES = [
    'consensus_reached',
    'decision_deferred',
    'family_declined_escalation',
    'no_consensus',
];
export const COMMUNICATION_OUTCOME_OPTIONS = [
    { key: 'consensus_reached', label: 'Consensus reached' },
    { key: 'decision_deferred', label: 'Decision deferred' },
    { key: 'family_declined_escalation', label: 'Family declined further escalation' },
    { key: 'no_consensus', label: 'No consensus' },
];
/* ------------------------------------------------------------ learner grade */
/**
 * Who the trainee taught or supervised during a case.
 *
 * The vocabulary is the union of the two logbooks trainees arrive with — see
 * `docs/IMPORT_SOURCES.md` — flattened to grades that mean something outside
 * anaesthetics. The Excel logbook splits ST3–4 from ST5–7; both are
 * `higher_trainee` here, because this app does not model training stages (see
 * the vocabulary rules in CLAUDE.md) and the distinction is not evidence of
 * anything a panel reads.
 *
 * Multi-select: teaching a student and a nurse in the same case is one event.
 *
 * `accp` — Advanced Critical Care Practitioner — added 2026-08-19 for the
 * procedure-level version of this field. Placed with `nurse`/`ahp` rather than
 * on the medical training ladder: an ACCP is a distinct, extended-scope role
 * usually drawn from a nursing or AHP background, not a rung between core and
 * higher trainee.
 */
export const LEARNER_GRADES = [
    'medical_student',
    'nurse',
    'ahp',
    'accp',
    'foundation_doctor',
    'core_trainee',
    'higher_trainee',
    'sas_or_non_trainee',
    'consultant',
    'other',
];
/** Display order runs junior to senior — the order people think in. */
export const LEARNER_GRADE_OPTIONS = [
    { key: 'medical_student', label: 'Medical student' },
    { key: 'nurse', label: 'Nurse' },
    { key: 'ahp', label: 'Allied health professional' },
    { key: 'accp', label: 'Advanced critical care practitioner' },
    { key: 'foundation_doctor', label: 'Foundation doctor' },
    { key: 'core_trainee', label: 'Core trainee' },
    { key: 'higher_trainee', label: 'Higher trainee' },
    { key: 'sas_or_non_trainee', label: 'SAS or non-trainee' },
    { key: 'consultant', label: 'Consultant' },
    { key: 'other', label: 'Other' },
];
/* -------------------------------------------------------------- anaesthetics */
/**
 * Where an anaesthetic case happened.
 *
 * `setting` rather than `activity`, which an EDT entry already uses as its
 * discriminator — two record types carrying a required field of the same name
 * would make the type guards in types.ts ambiguous, and the one that broke
 * would break silently.
 */
export const ANAESTHETIC_SETTINGS = ['theatre', 'acute_pain'];
export const ANAESTHETIC_SETTING_OPTIONS = [
    { key: 'theatre', label: 'Theatre' },
    { key: 'acute_pain', label: 'Acute pain' },
];
/**
 * NCEPOD classification, which is what the Anaesthetics platform records and
 * what an anaesthetic logbook is read for.
 */
export const ANAESTHETIC_PRIORITIES = ['elective', 'expedited', 'urgent', 'immediate'];
/** Least urgent first, which is how the classification is taught and read. */
export const ANAESTHETIC_PRIORITY_OPTIONS = [
    { key: 'elective', label: 'Elective' },
    { key: 'expedited', label: 'Expedited' },
    { key: 'urgent', label: 'Urgent' },
    { key: 'immediate', label: 'Immediate' },
];
/**
 * ASA physical status, 1 to 5.
 *
 * A grade of the patient's fitness, not an identifier — the same reasoning that
 * allows an age band. ASA 6 (declared brain-dead, organ retrieval) is omitted:
 * it is recorded as a Case with `organ_donation` in this app, and offering it
 * here would split one kind of event across two record types.
 */
export const ASA_GRADES = [1, 2, 3, 4, 5];
/* ------------------------------------------------------------- derived time */
export const TIMES_OF_DAY = ['day', 'night'];
export const DAY_PARTS = ['weekday_day', 'weekday_night', 'weekend_day', 'weekend_night'];
export const DAY_PART_LABELS = {
    weekday_day: 'Weekday day',
    weekday_night: 'Weekday night',
    weekend_day: 'Weekend day',
    weekend_night: 'Weekend night',
};
/** Look-up helpers. Prefer these to hand-written find calls in components. */
export function labelFor(options, key) {
    return options.find((option) => option.key === key)?.label ?? key;
}
//# sourceMappingURL=enums.js.map